import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { UserProfileStore } from '../../states/user-profile/user-profile.store';
import { AuthenticationStore } from '../../states/auth/auth.store';
import { EmployeeSettingsService } from '../../services/settings/employee-settings.service';
import { SetupAttentionService } from '../../services/setup/setup-attention.service';
import { GuidedTourService } from '../../services/tour/guided-tour.service';
import { WikiAttentionService } from '../../services/wiki/wiki-attention.service';

@Component({
  selector: 'app-main-layout',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private routerSub?: Subscription;
  readonly userProfileStore = inject(UserProfileStore);
  private readonly authStore = inject(AuthenticationStore);
  readonly settings = inject(EmployeeSettingsService);
  readonly setupAttentionService = inject(SetupAttentionService);
  readonly guidedTourService = inject(GuidedTourService);
  readonly wikiAttentionService = inject(WikiAttentionService);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    effect(() => {
      if (this.guidedTourService.isActive()) {
        this.sidebarCollapsed = false;
        this.mobileOpen = false;
        this.cdr.markForCheck();
      }
    });

    effect(() => {
      const revision = this.settings.workspaceRevision();
      if (revision === 0 || this.guidedTourService.isActive()) return;
      this.sidebarCollapsed = this.settings.collapseSidebarByDefault();
      this.cdr.markForCheck();
    });
  }

  get hasNoAccess(): boolean {
    const profile = this.userProfileStore.userProfile();
    if (!profile) return false;
    if (this.userProfileStore.roleName().toLowerCase() === 'employee') return false;
    // On employee-portal paths (/me/...) all roles are welcome — skip the features check
    if (this.router.url.startsWith('/me/')) return false;
    return this.userProfileStore.roleFeatures().length === 0;
  }

  sidebarCollapsed = false;
  mobileOpen = false;

  onMenuToggle(): void {
    if (window.innerWidth < 768) {
      this.mobileOpen = !this.mobileOpen;
    } else {
      this.sidebarCollapsed = !this.sidebarCollapsed;
    }
  }

  ngOnInit(): void {
    const guid = this.userProfileStore.userProfileGuid()
      || this.authStore.authToken()?.profile.userProfileGuid
      || '';
    this.settings.ensureLoaded(guid);
    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => { this.mobileOpen = false; this.cdr.markForCheck(); });
  }

  closeSetupModal(): void {
    this.setupAttentionService.closeForSession();
    this.cdr.markForCheck();
  }

  closeWikiAttentionModal(): void {
    this.wikiAttentionService.closeForSession();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void { this.routerSub?.unsubscribe(); }
}
